<script lang="ts">
  import type { ContentBlock } from "$lib/gql/genCms/codegen";
  import { ContentType } from "$lib/gql/genCms/codegen";
  import Carousel from "$lib/components/Carousel/Carousel.svelte";
  import ShowcaseFeature, {
    ContentSlotPlacement,
  } from "$lib/components/ShowcaseFeature/ShowcaseFeature.svelte";
  import TwoImageHero from "$lib/components/TwoImageHero/TwoImageHero.svelte";
  import Hero from "$lib/components/Hero/Hero.svelte";
  import TopSpacer from "$lib/components/TopSpacer/TopSpacer.svelte";
  import TextWithCta from "$lib/components/TextWithCta/TextWithCta.svelte";
  import CampaignFilm from "$lib/components/CampaignFilm/CampaignFilm.svelte";
  import ShopTheLook from "$lib/components/ShopTheLook/ShopTheLook.svelte";
  import type { ContentBlocksFragment } from "$lib/gql/genCms/codegen";
  import DestinationFeature from "../DestinationFeature/DestinationFeature.svelte";
  import Article from "../Article/Article.svelte";
  import QA from "../QA/QA.svelte";
  import RecommendationCarousel from "../Nosto/RecommendationCarousel.svelte";
  import DynamicGrid from "../DynamicGrid/DynamicGrid.svelte";
  import TabbedRecomendationCarousel from "../Nosto/TabbedRecomendationCarousel.svelte";

  export let contentBlocks: ContentBlock[] | ContentBlocksFragment[] = [];

  export let contentSlotPlacement: ContentSlotPlacement =
    ContentSlotPlacement.None;

  const headingElement = (i: number): string => {
    if (i === 0) {
      return "h1";
    }
    return "h2";
  };
</script>

{#if contentBlocks && contentBlocks.length && contentBlocks.length > 0}
  {#each contentBlocks as block, i}
    {#if block.contentType === ContentType.PromoPrimary && block.block.__typename === "PromoBlock"}
      <Hero
        smImage={block.block.imageSm}
        mdImage={block.block.imageSm}
        lgImage={block.block.imageLg}
        imgAltText={block.block.imageSm && block.block.imageSm.altText
          ? block.block.imageSm.altText
          : block.block.imageLg && block.block.imageLg.altText
            ? block.block.imageLg.altText
            : block.block.title}
        title={block.block.title}
        subtitle={block.block.subtitle}
        link1={block.block.link1}
        link2={block.block.link2}
        titleCapitalise={false}
        imgFromUrl
        headingElement={headingElement(i)}
      />
    {:else if block.contentType === ContentType.SingleFeatureBlock && block.block.__typename === "SingleFeatureBlock"}
      <ShowcaseFeature
        mediaType={block.block.mediaType}
        image={block.block?.image}
        imageSm={block.block?.imageSm}
        imageAlt={block.block?.image?.altText}
        title={block.block?.title}
        subtitle={block.block?.subtitle}
        body={block.block?.text && block.block.text?.html}
        ctaOneLink={block.block?.cta?.href}
        ctaOneText={block.block?.cta?.text}
        ctaTwoLink={block.block?.cta2 && block.block.cta2?.href}
        ctaTwoText={block.block?.cta2 && block.block.cta2?.text}
        position={block.block.position}
        alignment={block.block.alignment}
        invert={block.block.invert}
        headingElement={headingElement(i)}
        videoSm={block.block?.videoSm}
        video={block.block?.video}
        blockCtaLink={block.block?.blockCta?.href && block.block.blockCta.href}
        blockCtaText={block.block?.blockCta?.text && block.block.blockCta.text}
        {contentSlotPlacement}
        rectangleDivider={block.block?.rectangleDivider}
        ctaOneBgColour={block.block?.cta?.bgColour}
        ctaTwoBgColour={block.block?.cta2?.bgColour}
        ctaOneTextLight={block.block?.cta?.textLight}
        ctaTwoTextLight={block.block?.cta2?.textLight}
      />
    {:else if block.contentType === ContentType.ShopTheLookBlock && block.block.__typename === "ShopTheLook"}
      <ShopTheLook
        mediaType={block.block.mediaType}
        isFirstBlock={i === 0}
        image={block.block?.image}
        imageSm={block.block?.imageSm}
        imageAlt={block.block?.image?.altText}
        title={block.block?.title}
        body={block.block?.text && block.block.text?.html}
        ctaOneLink={block.block?.cta?.href}
        ctaOneText={block.block?.cta?.text}
        ctaTwoLink={block.block?.cta2 && block.block.cta2?.href}
        ctaTwoText={block.block?.cta2 && block.block.cta2?.text}
        invert={block.block.invert}
        headingElement={headingElement(i)}
        videoSm={block.block?.videoSm}
        video={block.block?.video}
        cards={block.block.cards}
        url={block.block?.collection && block.block?.collection?.url}
        blockCtaLink={block.block?.blockCta?.href && block.block.blockCta.href}
        blockCtaText={block.block?.blockCta?.text && block.block.blockCta.text}
        {contentSlotPlacement}
        rectangleDivider={block.block?.rectangleDivider}
        ctaOneBgColour={block.block?.cta?.bgColour}
        ctaTwoBgColour={block.block?.cta2?.bgColour}
        ctaOneTextLight={block.block?.cta?.textLight}
        ctaTwoTextLight={block.block?.cta2?.textLight}
      />
    {:else if block.contentType === ContentType.ProductCarousel && block.block.__typename === "Carousel"}
      {#if block.block.cards && block.block.cards.edges && block.block.cards.edges.length > 0}
        <Carousel
          id={block.id}
          title={block.block.title}
          cta={block.block.cta}
          size={block.block.size}
          cards={block.block.cards}
          url={block.block.collection && block.block.collection.url}
          headingElement={headingElement(i)}
          quickBuy={true}
          total={block.block.cards.totalCount}
          key={block.id}
        />
      {/if}
    {:else if block.contentType === ContentType.TextWithCtaBlock && block.block.__typename === "TextWithCtaBlock"}
      <TextWithCta
        title={block.block.title}
        text={block.block.text && block.block.text.html}
        align={block.block.align}
        link1={block.block.link1}
        link2={block.block.link2}
        headingElement={headingElement(i)}
        rectangleDivider={block.block?.rectangleDivider}
        ctaOneBgColour={block.block?.link1?.bgColour}
        ctaTwoBgColour={block.block?.link2?.bgColour}
        ctaOneTextLight={block.block?.link1?.textLight}
        ctaTwoTextLight={block.block?.link2?.textLight}
      />
    {:else if block.contentType === ContentType.TwoImageHeroBlock && block.block.__typename === "TwoImageHeroBlock"}
      <TwoImageHero
        title={block.block.title}
        cta={block.block.cta}
        image={block.block.image}
        title2={block.block.title2}
        cta2={block.block.cta2}
        image2={block.block.image2}
        invert={block.block.invert}
      />
    {:else if block.contentType === ContentType.FilmBlock && block.block.__typename === "FilmBlock"}
      <CampaignFilm
        sources={[
          {
            title: block.block.title,
            poster: block.block.coverImageSm.uri,
            src: block.block.videoSm.uri,
            minWidth: 0,
            maxWidth: 1279,
            type: "video/mp4",
            invert: false,
          },
          {
            title: block.block.title,
            poster: block.block.coverImage.uri,
            src: block.block.video.uri,
            minWidth: 1280,
            maxWidth: 10000,
            type: "video/mp4",
            invert: false,
          },
        ]}
      />
    {:else if block.contentType === ContentType.DestinationFeatureBlock && block.block.__typename === "DestinationFeature"}
      <DestinationFeature
        mediaType={block.block.mediaType}
        isFirstBlock={i === 0}
        id={block.id}
        image={block.block?.image}
        imageSm={block.block?.imageSm}
        imageAlt={block.block?.image?.altText}
        title={block.block?.title}
        subtitle={block.block?.subtitle}
        body={block.block?.text && block.block.text?.html}
        ctaOneLink={block.block?.cta?.href}
        ctaOneText={block.block?.cta?.text}
        ctaTwoLink={block.block?.cta2 && block.block.cta2?.href}
        ctaTwoText={block.block?.cta2 && block.block.cta2?.text}
        invert={block.block.invert}
        headingElement={headingElement(i)}
        videoSm={block.block?.videoSm}
        video={block.block?.video}
        cards={block.block?.cards}
        url={block.block?.collection && block.block?.collection?.url}
        blockCtaLink={block.block?.blockCta?.href && block.block.blockCta.href}
        blockCtaText={block.block?.blockCta?.text && block.block.blockCta.text}
        rectangleDivider={block.block.rectangleDivider}
        ctaOneBgColour={block.block?.cta?.bgColour}
        ctaTwoBgColour={block.block?.cta2?.bgColour}
        ctaOneTextLight={block.block?.cta?.textLight}
        ctaTwoTextLight={block.block?.cta2?.textLight}
      />
    {:else if block.contentType === ContentType.ArticleBlock && block.block.__typename === "ArticleBlock"}
      <Article
        title={block.block.title}
        subtitle={block.block.subtitle}
        cta={block.block.cta}
        text={block.block.text && block.block.text.html}
        image={block.block.image}
        invert={block.block.invert}
        position={block.block.position}
        alignment={block.block.alignment}
        reverse={block.block.reverse}
        textPosition={block.block.textPosition}
      />
    {:else if block.contentType === ContentType.QaBlock && block.block.__typename === "QABlock"}
      <QA
        question1={block.block.question1}
        answer1={block.block.answer1}
        question2={block.block.question2}
        answer2={block.block.answer2}
        question3={block.block.question3}
        answer3={block.block.answer3}
        question4={block.block.question4}
        answer4={block.block.answer4}
        cta={block.block.cta}
        image={block.block.image}
        invert={block.block.invert}
        position={block.block.position}
        alignment={block.block.alignment}
        reverse={block.block.reverse}
        textPosition={block.block.textPosition}
      />
    {:else if block.contentType === ContentType.RecommendationBlock && block.block.__typename === "RecommendationBlock"}
      <RecommendationCarousel
        pageType="LANDING"
        input={{
          placement: block.block.placementId,
        }}
        title={block.block.title}
        cta={block.block.cta}
        carouselSize={block.block.size}
        ctaPosition={block.block.position}
      />
    {:else if block.contentType === ContentType.DynamicGridBlock && block.block.__typename === "DynamicGrid"}
      <DynamicGrid
        cards={block.block.cards.edges}
        autoscroll={block.block.autoscroll}
        speed={block.block.speed}
      />
      <span />
    {:else if block.contentType === ContentType.TabbedRecommendationBlock && block.block.__typename === "TabbedRecommendation"}
      <TabbedRecomendationCarousel
        carousels={block.block.cards.edges.map(edge => {
          if (edge.node.content.__typename !== "TabbedRecommendationCard") {
            return null;
          }
          return {
            pageType: "HOME",
            input: { placement: edge.node.content.placementId },
            carouselSize: edge.node.content.size,
            tabTitle: edge.node.content.title,
            displayTitle: edge.node.content.displayNostoTitle,
          };
        })}
      />
    {/if}
  {/each}
{:else}
  <TopSpacer />
{/if}
